// Documenti della pratica: testi, date e file (funzioni pure, tranne scarica/apri che usano il browser).
// Il catalogo dei tipi è frontend/src/data/catalogo_documenti.json (lo stesso file del backend).
//
// Lingua: qui non ci sono componenti, quindi `lingua` è sempre l'ULTIMO parametro, con l'italiano di base. Chi
// chiama senza dirla (la pagina dello staff, i vecchi richiami) resta in italiano com'era. I nomi dei documenti e
// il «a cosa serve» NON stanno qui: arrivano già tradotti dal catalogo del server.
import catalogo from '../data/catalogo_documenti.json';
import { PRINCIPALE, contesto, dataIn, traduci } from '../i18n';
import { API, axios } from './api';
import { adessoRoma, conAnno, conteggioEntro, ilGiorno, leggiDataUtcRoma } from './percorso';

export const CATALOGO = catalogo;
export const CATEGORIE = Object.fromEntries(catalogo.categorie.map((c) => [c.id, c.nome]));
export const MAX_BYTES = 10 * 1024 * 1024;
const LATO_MASSIMO = 2200;
const MESI_BREVI = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
const MESI = ['gennaio', 'febbraio', 'marzo', 'aprile', 'maggio', 'giugno', 'luglio', 'agosto', 'settembre', 'ottobre', 'novembre', 'dicembre'];

const NUMERI = ['', 'uno', 'due', 'tre', 'quattro', 'cinque', 'sei', 'sette', 'otto', 'nove', 'dieci'];

// "Ne mancano 2, il resto c'è" (la parte in corsivo a parte, per l'<em>). Nei dizionari il pezzo da mettere in
// corsivo sta fra parentesi quadre: "Ne mancano [{n}], il resto c'è". Così ogni lingua decide dove mettere il
// numero (in russo e in ucraino la parola cambia con il numero: il plurale lo fa tp).
const spezza = (testo) => {
  const pezzi = /^(.*?)\[(.*?)\](.*)$/.exec(String(testo));
  return pezzi ? { prima: pezzi[1], em: pezzi[2], dopo: pezzi[3] } : { prima: '', em: String(testo), dopo: '' };
};
export const titoloMancano = (mancano, ricevuti, lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  if (!mancano) return spezza(t(ricevuti ? 'documenti.titolo_tutti' : 'documenti.titolo_niente'));
  return spezza(tp(ricevuti ? 'documenti.plurali.mancano' : 'documenti.plurali.servono', mancano));
};

export const numeroInLettere = (n) => NUMERI[n] || String(n);

// "Maksym e Karolina", "Maksym, Karolina e Paolo", "Maksym ed Ernesto" ("ed" davanti a una parola che comincia con
// la e: regola solo italiana; nelle altre lingue la congiunzione è quella del dizionario)
export const elenco = (parole, lingua = PRINCIPALE) => {
  const p = (parole || []).filter(Boolean);
  if (p.length <= 1) return p.join('');
  const ultima = p[p.length - 1];
  const e = lingua === PRINCIPALE ? (/^[eèé]/i.test(ultima) ? 'ed' : 'e') : traduci(lingua, 'comune.e');
  return `${p.slice(0, -1).join(', ')} ${e} ${ultima}`;
};

export const comeData = (valore) => {
  if (!valore) return null;
  // Le date senza fuso del backend sono UTC (MongoDB)
  const testo = typeof valore === 'string' && /T\d\d:\d\d(:\d\d(\.\d+)?)?$/.test(valore) ? `${valore}Z` : valore;
  const d = new Date(testo);
  return Number.isNaN(d.getTime()) ? null : d;
};

// Le date si mostrano nell'ora di Roma, come le registra il server (il PDF firmato dice "16:10 ora italiana"): con il
// telefono a New York la cronologia diceva "10:10". `inRoma` = un Date i cui campi (giorno, ore) sono quelli di Roma
// (leggiDataUtcRoma di utils/percorso.js, dopo la lettura tollerante di comeData); `oggi` va dato allo stesso modo
// (adessoRoma). Sul fuso di Roma non cambia niente.
export const inRoma = (valore) => {
  const d = comeData(valore);
  return d ? leggiDataUtcRoma(d) : null;
};
const altroAnno = (d, oggi) => d.getFullYear() !== oggi.getFullYear();
const giornoBreveDi = (d, oggi) => `${d.getDate()} ${MESI_BREVI[d.getMonth()]}${altroAnno(d, oggi) ? ` ${d.getFullYear()}` : ''}`;
const due = (n) => String(n).padStart(2, '0');

// Il giorno breve nella lingua chiesta. Per l'italiano restano le parole di sempre; per le altre lingue è Intl
// (i18n.dataIn), a cui va dato il valore com'è arrivato dal server — non la data già spostata su Roma, che
// verrebbe spostata una seconda volta.
const breve = (valore, d, oggi, lingua) => (lingua === PRINCIPALE ? giornoBreveDi(d, oggi)
  : dataIn(lingua, comeData(valore), { day: 'numeric', month: 'short', ...(altroAnno(d, oggi) ? { year: 'numeric' } : {}) }));

// "12 ago" (stesso anno) o "12 ago 2025"
export const giornoBreve = (valore, oggi = adessoRoma(), lingua = PRINCIPALE) => {
  const d = inRoma(valore);
  return d ? breve(valore, d, oggi, lingua) : '';
};

// "15 settembre alle 16:10" (ora di Roma): la firma dal telefono. Di un altro anno con l'anno (conAnno):
// "15 settembre 2025 alle 16:10"
export const giornoEOra = (valore, oggi = adessoRoma(), lingua = PRINCIPALE) => {
  const d = inRoma(valore);
  if (!d) return '';
  const ora = `${due(d.getHours())}:${due(d.getMinutes())}`;
  if (lingua === PRINCIPALE) return `${conAnno(`${d.getDate()} ${MESI[d.getMonth()]}`, d, oggi)} alle ${ora}`;
  const giorno = dataIn(lingua, comeData(valore), { day: 'numeric', month: 'long', ...(altroAnno(d, oggi) ? { year: 'numeric' } : {}) });
  return contesto(lingua).t('documenti.quando.alle', { giorno, ora });
};

// "Oggi · 09:40", "Ieri · 17:20", "5 set"
export const quando = (valore, oggi = adessoRoma(), lingua = PRINCIPALE) => {
  const d = inRoma(valore);
  if (!d) return '';
  const ora = `${due(d.getHours())}:${due(d.getMinutes())}`;
  const giorno = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((giorno(oggi) - giorno(d)) / 86400000);
  const { t } = contesto(lingua);
  if (diff === 0) return t('documenti.quando.oggi', { ora });
  if (diff === 1) return t('documenti.quando.ieri', { ora });
  return breve(valore, d, oggi, lingua);
};

// "chiesto ieri", "chiesto il 5 set"
export const chiestoQuando = (valore, oggi = new Date()) => {
  const q = quando(valore, oggi);
  if (q.startsWith('Oggi')) return 'chiesto oggi';
  if (q.startsWith('Ieri')) return 'chiesto ieri';
  return q ? `chiesto ${ilGiorno(q)}` : '';
};

// "sollecito automatico tra 2 giorni" / "domani" / "oggi"
export const prossimoSollecito = (valore, oggi = new Date()) => {
  const d = comeData(valore);
  if (!d) return '';
  const giorno = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const giorni = Math.round((giorno(d) - giorno(oggi)) / 86400000);
  if (giorni <= 0) return 'sollecito automatico oggi';
  if (giorni === 1) return 'sollecito automatico domani';
  return `sollecito automatico tra ${giorni} giorni`;
};

// Documenti che aspettano lo staff (i vecchi, caricati prima delle verifiche, valgono come ricevuti)
const inAttesa = (d) => ['da_verificare', 'da_classificare'].includes(d.stato) || d.in_analisi;

// "il 12 ago" in italiano (ilGiorno mette l'articolo), la sola data nelle altre lingue
const ilGiornoIn = (valore, oggi, lingua) => (lingua === PRINCIPALE
  ? ilGiorno(giornoBreve(valore, oggi)) : giornoBreve(valore, oggi, lingua));

// Una riga di "Ricevuti": chi l'ha verificato, a chi è stato consegnato, la scadenza
export const descriviVoce = (voce, oggi = new Date(), lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const docs = voce.documenti || [];
  const pezzi = [];
  if (voce.periodico?.testo_periodo) pezzi.push(voce.periodico.testo_periodo);
  // «dal telefono» solo se è stato firmato davvero nell'app (dal_telefono: il server lo sa dalla firma); un modulo
  // firmato su carta e caricato dallo staff (la scansione) è «firmato il…» (prima anche lui «dal telefono»)
  if (voce.tipo === 'documento_firmato' && docs.length) {
    const ultimo = docs[docs.length - 1];
    return t(ultimo.dal_telefono ? 'documenti.stato.firmato_telefono' : 'documenti.stato.firmato',
      { quando: ilGiornoIn(ultimo.data_upload, oggi, lingua) });
  }
  const verificati = docs.filter((d) => d.stato === 'verificato');
  const rifiutato = docs.find((d) => d.stato === 'rifiutato');
  if (rifiutato && !verificati.length) {
    pezzi.push(rifiutato.rifiuto?.motivo
      ? t('documenti.stato.da_ricaricare_motivo', { motivo: rifiutato.rifiuto.motivo }) : t('documenti.stato.da_ricaricare'));
  } else if (docs.some((d) => d.in_analisi)) pezzi.push(t('documenti.stato.in_lettura'));
  else if (docs.some(inAttesa)) pezzi.push(t('documenti.stato.in_verifica'));
  else if (verificati.length) {
    const ultimo = verificati.map((d) => d.verificato).filter(Boolean).sort((a, b) => String(b.data).localeCompare(String(a.data)))[0];
    pezzi.push(ultimo?.nome
      ? t(docs.length > 1 ? 'documenti.stato.verificati_da' : 'documenti.stato.verificato_da',
        { nome: ultimo.nome, quando: ilGiornoIn(ultimo.data, oggi, lingua) })
      : t('documenti.stato.verificato'));
  } else pezzi.push(t(docs.length > 1 ? 'documenti.stato.ricevuti' : 'documenti.stato.ricevuto')); // caricati prima delle verifiche
  return pezzi.join(' · ');
};

export const consegna = (voce, oggi = new Date(), lingua = PRINCIPALE) => {
  const c = (voce.consegne || []).slice().sort((a, b) => String(b.data).localeCompare(String(a.data)))[0];
  return c ? contesto(lingua).t('documenti.stato.consegnato', { ente: c.ente, quando: ilGiornoIn(c.data, oggi, lingua) }) : null;
};

export const scadenzaDiVoce = (voce) => {
  const docs = (voce.documenti || []).filter((d) => d.scadenza_testo);
  return docs.find((d) => d.scadenza_stato === 'scaduto') || docs.find((d) => d.scadenza_stato === 'presto') || docs[0] || null;
};

// Stato dell'icona della riga: ok (verificato), wait (in verifica / da ricaricare), sent (consegnato)
export const iconaVoce = (voce) => {
  const docs = voce.documenti || [];
  const pronti = docs.length && !docs.some((d) => inAttesa(d) || d.stato === 'rifiutato');
  if (pronti && (voce.consegne || []).length) return 'sent';
  return pronti ? 'ok' : 'wait';
};

// ---------------------------------------------------------------- conteggi in italiano

// "1 documento" / "3 documenti": il numero con la parola al singolare o al plurale. Una volta sola, perché
// "1 richieste aperte" e "1 ricevuti" si scrivevano a mano in giro per le pagine dei documenti.
export const conteggio = (n, singolare, plurale) => `${n} ${n === 1 ? singolare : plurale}`;

// ---------------------------------------------------------------- di chi è (le persone del nucleo)

// "2 ricevuti · 1 manca · 1 richiesta aperta": i conteggi di una persona (quello che vale zero non si dice).
// I numeri passano dai plurali veri: in russo e in ucraino la parola cambia con 1, 2 e 5.
export const contiPersona = (p = {}, lingua = PRINCIPALE) => {
  const { t, tp } = contesto(lingua);
  const parti = [];
  if (p.ricevuti) parti.push(tp('documenti.plurali.ricevuti', p.ricevuti));
  if (p.mancano) parti.push(tp('documenti.plurali.mancano_conto', p.mancano));
  if (p.richieste_aperte) parti.push(tp('documenti.plurali.richieste', p.richieste_aperte));
  return parti.join(' · ') || t('documenti.niente_ancora');
};

// Il nome di una persona nel titoletto del gruppo: "Olena Prova · coniuge". Per quelli che non sono di nessuno il
// titoletto non ripete la domanda del riquadro sotto ("Di chi è questo documento?"), dice come stanno le cose
export const titoloPersona = (p = {}, lingua = PRINCIPALE) => {
  if (p.senza_persona || !p.nome) return traduci(lingua, 'documenti.non_sappiamo_di_chi');
  return p.etichetta && p.etichetta !== 'titolare' ? `${p.nome} · ${p.etichetta}` : p.nome;
};

// Le righe di "Ricevuti" raggruppate per persona, nell'ordine che dà il server (il titolare, il resto del nucleo e in
// fondo quelli di cui non si sa di chi sono). Ogni riga si porta dietro la sua categoria, per il titoletto piccolo.
export const ricevutiPerPersona = (ricevuti, persone) => {
  const per = new Map((persone || []).map((p) => [p.senza_persona ? null : p.id, { persona: p, voci: [] }]));
  (ricevuti || []).forEach((g) => (g.voci || []).forEach((v) => {
    const chiave = v.senza_persona ? null : (v.persona_id ?? null);
    if (!per.has(chiave)) per.set(chiave, { persona: { id: chiave, nome: v.persona, senza_persona: !!v.senza_persona }, voci: [] });
    per.get(chiave).voci.push({ ...v, categoria_nome: g.nome });
  }));
  return [...per.values()].filter((x) => x.voci.length || x.persona.documenti);
};

// Si raggruppa per persona solo se le persone sono più di una (o se c'è un documento di cui non si sa di chi è):
// con il solo titolare i gruppi restano quelli per categoria, come prima
export const raggruppaPerPersona = (persone) => (persone || []).length > 1 || (persone || []).some((p) => p.senza_persona);

// "settembre 2026": il mese con l'anno. Per l'italiano le parole di sempre; per le altre lingue Intl, a cui si dà
// il 15 del mese a mezzogiorno UTC, così l'ora di Roma non fa mai scivolare l'etichetta al mese prima o dopo.
const meseEAnno = (anno, mese, lingua) => (lingua === PRINCIPALE ? `${MESI[mese]} ${anno}`
  : dataIn(lingua, new Date(Date.UTC(anno, mese, 15, 12)), { month: 'long', year: 'numeric' }));

// Il periodo che dice il server arriva con l'etichetta in italiano ("giugno 2026", "2° trimestre 2026"): per le
// altre lingue la riscriviamo qui dal periodo stesso. Quello che non sappiamo rifare (gli intervalli, che sono
// solo date) resta com'è arrivato.
export const etichettaPeriodo = (periodo, ripiego = '', lingua = PRINCIPALE) => {
  if (lingua === PRINCIPALE || !periodo) return ripiego;
  const { t } = contesto(lingua);
  if (periodo.tipo === 'mese' && periodo.anno && periodo.mese) return meseEAnno(periodo.anno, periodo.mese - 1, lingua);
  if (periodo.tipo === 'anno' && periodo.anno) return String(periodo.anno);
  if (periodo.tipo === 'trimestre' && periodo.anno && periodo.trimestre) return t('documenti.periodo.trimestre_n', { n: periodo.trimestre, anno: periodo.anno });
  return ripiego;
};

// Mesi, trimestri e intervalli per impostare il periodo di un documento periodico con un clic
export const sceltePeriodo = (regola, oggi = new Date(), lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const scelte = [];
  if (regola === 'ultimi_mesi') {
    for (let i = 1; i <= 6; i += 1) {
      const d = new Date(oggi.getFullYear(), oggi.getMonth() - i, 1);
      scelte.push({ etichetta: meseEAnno(d.getFullYear(), d.getMonth(), lingua), periodo: { tipo: 'mese', anno: d.getFullYear(), mese: d.getMonth() + 1 } });
    }
  } else if (regola === 'ultimo_anno') {
    for (let a = oggi.getFullYear(); a >= oggi.getFullYear() - 2; a -= 1) scelte.push({ etichetta: String(a), periodo: { tipo: 'anno', anno: a } });
  } else if (regola === 'trimestre_chiuso') {
    let anno = oggi.getFullYear();
    let q = Math.floor(oggi.getMonth() / 3);
    for (let i = 0; i < 4; i += 1) {
      if (q === 0) { anno -= 1; q = 4; }
      scelte.push({ etichetta: t('documenti.periodo.trimestre_n', { n: q, anno }), periodo: { tipo: 'trimestre', anno, trimestre: q } });
      q -= 1;
    }
  } else if (regola === 'movimenti_dopo_trimestre') {
    const fine = new Date(oggi.getFullYear(), Math.floor(oggi.getMonth() / 3) * 3, 1);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    scelte.push({
      etichetta: t('documenti.periodo.dal_a_oggi', { giorno: `${fine.getDate()}/${String(fine.getMonth() + 1).padStart(2, '0')}` }),
      periodo: { tipo: 'intervallo', dal: iso(fine), al: iso(oggi) },
    });
  }
  return scelte;
};

// ---------------------------------------------------------------- file

const eImmagine = (file) => /^image\/(jpeg|png)$/.test(file.type);

// Le foto grandi si rimpiccioliscono (lato lungo 2200 px, JPEG 85%) prima di partire: restano leggibili e
// pesano molto meno. HEIC e PDF partono come sono (il server converte l'HEIC e unisce più file in un PDF).
export const preparaFile = async (file) => {
  if (!eImmagine(file) || typeof createImageBitmap !== 'function') return file;
  try {
    const immagine = await createImageBitmap(file);
    const scala = Math.min(1, LATO_MASSIMO / Math.max(immagine.width, immagine.height));
    if (scala === 1 && file.size < 1.5 * 1024 * 1024) return file;
    const tela = document.createElement('canvas');
    tela.width = Math.round(immagine.width * scala);
    tela.height = Math.round(immagine.height * scala);
    tela.getContext('2d').drawImage(immagine, 0, 0, tela.width, tela.height);
    const blob = await new Promise((ok) => tela.toBlob(ok, 'image/jpeg', 0.85));
    return blob && blob.size < file.size ? new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
};

export const controllaFile = (files, lingua = PRINCIPALE) => {
  const { t } = contesto(lingua);
  const totale = files.reduce((s, f) => s + (f.size || 0), 0);
  if (!files.length) return t('documenti.file.nessuno');
  if (files.some((f) => f.size > MAX_BYTES) || totale > MAX_BYTES) return t('documenti.file.troppo_grande');
  if (files.some((f) => f.type && !/^(application\/pdf|image\/(jpeg|png|heic|heif))$/.test(f.type) && !/\.(pdf|jpe?g|png|heic|heif)$/i.test(f.name))) {
    return t('documenti.file.formati');
  }
  return null;
};

export const caricaDocumento = async (clienteId, files, { tipo, persona, personaId, periodo, comePdf, lingua = PRINCIPALE } = {}) => {
  const pronti = await Promise.all(files.map(preparaFile));
  const errore = controllaFile(pronti, lingua);
  if (errore) throw new Error(errore);
  const modulo = new FormData();
  pronti.forEach((f) => modulo.append('files', f, f.name || 'foto.jpg'));
  if (tipo) modulo.append('tipo', tipo);
  if (persona) modulo.append('persona_riferimento', persona);
  if (personaId) modulo.append('persona_id', personaId);
  if (comePdf) modulo.append('come_pdf', 'true');
  if (periodo) modulo.append('periodo', JSON.stringify(periodo));
  const r = await axios.post(`${API}/clienti/${clienteId}/documenti`, modulo);
  return r.data;
};

// Scarica con il login (il backend manda sempre un allegato); azione "apri" = anteprima, finisce nel log
export const leggiDocumento = async (doc, azione = 'scarica') => {
  const r = await axios.get(`${API}/documenti/${doc.id}/download`, { params: { azione }, responseType: 'blob' });
  return r.data;
};

export const salvaBlob = (blob, nome) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome || 'documento';
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

// Il nome del file mandato dal server nel Content-Disposition: prima filename* (UTF-8, "documenti-olena-шевченко…"),
// poi filename; null se non c'è
export const nomeDaIntestazione = (intestazione) => {
  const testo = String(intestazione || '');
  const utf8 = testo.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
  if (utf8) {
    try { return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, '')); } catch { /* sotto il nome semplice */ }
  }
  const semplice = testo.match(/filename\s*=\s*"([^"]+)"|filename\s*=\s*([^;]+)/i);
  const nome = semplice && (semplice[1] || semplice[2] || '').trim();
  return nome || null;
};

// Lo zip col nome che dà il server ("documenti-oleksandr-marchenko.zip"); `nome` solo se il server non lo dice
export const scaricaZip = async (url, nome, dati) => {
  let r;
  try {
    r = dati ? await axios.post(url, dati, { responseType: 'blob' }) : await axios.get(url, { responseType: 'blob' });
  } catch (errore) {
    // con `responseType: blob` anche il messaggio d'errore del server arriva come Blob: senza leggerlo si mostrerebbe
    // sempre la frase di riserva invece di «Non c'è ancora nessun documento da scaricare»
    const corpo = errore?.response?.data;
    if (corpo && typeof corpo.text === 'function') {
      try {
        const testo = await corpo.text();
        errore.response.data = JSON.parse(testo);
      } catch { /* non era JSON: resta com'era */ }
    }
    throw errore;
  }
  const intestazioni = r.headers || {};
  const disposizione = typeof intestazioni.get === 'function' ? intestazioni.get('content-disposition') : intestazioni['content-disposition'];
  salvaBlob(r.data, nomeDaIntestazione(disposizione) || nome);
};

// "entro il 20 settembre · mancano 3 giorni" (richieste con la data): le parole della home (conteggioEntro di
// utils/percorso.js). Con la data passata «era da mandare entro il 12 settembre»: prima «scaduto il 12 settembre»,
// che sembrava il documento scaduto (mai caricato)
export const testoEntro = (entro, oggi = new Date(), lingua = PRINCIPALE) => {
  if (!entro) return null;
  const conto = conteggioEntro(entro, oggi);
  if (!conto) return null;
  const { giorni } = conto;
  const classe = giorni <= 0 ? 'no' : giorni <= 3 ? 'w' : 'b';
  if (lingua === PRINCIPALE) return { testo: conto.testo, giorni, classe };
  // nelle altre lingue le stesse parole, ma la data la scrive Intl (i18n) e i giorni passano dal plurale vero
  const { t, tp, data } = contesto(lingua);
  const [a, m, g] = String(entro).slice(0, 10).split('-').map(Number);
  const quandoData = new Date(Date.UTC(a, m - 1, g, 12));
  const giorno = data(quandoData, { day: 'numeric', month: 'long', ...(a !== oggi.getFullYear() ? { year: 'numeric' } : {}) });
  if (giorni < 0) return { testo: t('documenti.entro.passato', { data: giorno }), giorni, classe };
  if (giorni === 0) return { testo: t('documenti.entro.oggi'), giorni, classe };
  if (giorni === 1) return { testo: t('documenti.entro.domani'), giorni, classe };
  return { testo: t('documenti.entro.giorno_conto', { data: giorno, giorni: tp('comune.plurali.giorni', giorni) }), giorni, classe };
};
